import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { login } from '../../features/search/service/login-service';
import { requestPasswordResetCode, resetPasswordWithCode } from '../../features/search/service/password-reset-service';
import { consumeAuthError, PLAN_INACTIVE_CODE, PLAN_INACTIVE_MESSAGE, saveToken } from '../../shared/helpers/localstorage-helper';
import ButtonComponent from '../../components/button/button';
import InputComponent from '../../components/input/input';
import { Language } from '../../shared/i18n/translations';
import { useTranslation } from '../../shared/i18n/i18n-context';
import './styles.css';

type LoginMode = 'login' | 'forgotPassword' | 'resetPassword' | 'resetSuccess';
type LoginMessageKey =
    | 'requiredFields'
    | 'invalidCredentials'
    | 'planInactive'
    | 'invalidEmail'
    | 'resetCodeRequired'
    | 'invalidResetCode'
    | 'passwordRequired'
    | 'passwordMinLength'
    | 'passwordsDoNotMatch'
    | 'passwordResetRequestError'
    | 'passwordResetError'
    | 'resetCodeSent';

export default function Login() {
    const navigate = useNavigate();
    const location = useLocation();
    const { language, setLanguage, t } = useTranslation('login');

    const [email, setEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [resetCode, setResetCode] = useState<string>('');
    const [newPassword, setNewPassword] = useState<string>('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [mode, setMode] = useState<LoginMode>('login');
    const [error, setError] = useState<string>('');
    const [errorKey, setErrorKey] = useState<LoginMessageKey | null>(null);
    const [successKey, setSuccessKey] = useState<LoginMessageKey | null>(null);

    useEffect(() => {
        const routeState = location.state as { error?: string } | null;
        const storedError = consumeAuthError();

        const nextError = routeState?.error || storedError || '';

        if (nextError) {
            setErrorKey(null);
            setSuccessKey(null);
            if (nextError === PLAN_INACTIVE_MESSAGE) {
                setError('');
                setErrorKey('planInactive');
                return;
            }
            setError(nextError);
        }
    }, [location.state]);

    const clearMessages = () => {
        setError('');
        setErrorKey(null);
        setSuccessKey(null);
    };

    const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

    const handleLogin = async () => {
        if (!email || !password) {
            clearMessages();
            setErrorKey('requiredFields');
            return;
        }

        setLoading(true);
        clearMessages();

        try {
            const response = await login(email, password);
            let isFirstAccess = false;
            
            if(response.userWithoutPassword){
                isFirstAccess = response.userWithoutPassword.firstAccess || 
                                  response.userWithoutPassword.firstAcess;
            }
            
            if (!isFirstAccess && response.userWithoutPassword) {
                saveToken(response.token);
                navigate('/home');
            } else {
                navigate('/reset-pwd', {
                    state: { 
                        email: response.userWithoutPassword.email 
                    }
                });
            }   
        } catch (error) {   
            const loginError = error as Error & { code?: string; status?: number };

            if (loginError.code === PLAN_INACTIVE_CODE || loginError.status === 403) {
                clearMessages();
                setErrorKey('planInactive');
                return;
            }

            clearMessages();
            setErrorKey('invalidCredentials');
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = () => {
        clearMessages();
        setMode('forgotPassword');
    };

    const handleBackToLogin = () => {
        clearMessages();
        setMode('login');
        setResetCode('');
        setNewPassword('');
        setConfirmPassword('');
    };

    const handleRequestResetCode = async () => {
        if (!email || !isValidEmail(email)) {
            clearMessages();
            setErrorKey('invalidEmail');
            return;
        }

        setLoading(true);
        clearMessages();

        try {
            await requestPasswordResetCode(email);
            setSuccessKey('resetCodeSent');
            setMode('resetPassword');
        } catch (error) {
            setError(t('passwordResetRequestError'));
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async () => {
        if (!resetCode) {
            clearMessages();
            setErrorKey('resetCodeRequired');
            return;
        }

        if (!/^\d{6}$/.test(resetCode)) {
            clearMessages();
            setErrorKey('invalidResetCode');
            return;
        }

        if (!newPassword || !confirmPassword) {
            clearMessages();
            setErrorKey('passwordRequired');
            return;
        }

        if (newPassword.length < 6) {
            clearMessages();
            setErrorKey('passwordMinLength');
            return;
        }

        if (newPassword !== confirmPassword) {
            clearMessages();
            setErrorKey('passwordsDoNotMatch');
            return;
        }

        setLoading(true);
        clearMessages();

        try {
            await resetPasswordWithCode(email, resetCode, newPassword);
            setPassword('');
            setResetCode('');
            setNewPassword('');
            setConfirmPassword('');
            setMode('resetSuccess');
        } catch (error) {
            setError(t('passwordResetError'));
        } finally {
            setLoading(false);
        }
    };

    const handleLanguageChange = (selectedLanguage: Language) => {
        setLanguage(selectedLanguage);
    };

    const titleKey = mode === 'forgotPassword'
        ? 'forgotPasswordTitle'
        : mode === 'resetPassword'
            ? 'resetPasswordTitle'
            : mode === 'resetSuccess'
                ? 'resetPasswordSuccessTitle'
                : 'title';
    const errorMessage = errorKey ? t(errorKey) : error;
    const successMessage = successKey ? t(successKey) : '';

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();

            if (mode === 'login') {
                handleLogin();
                return;
            }

            if (mode === 'forgotPassword') {
                handleRequestResetCode();
                return;
            }

            if (mode === 'resetPassword') {
                handleResetPassword();
            }
        }
    };

    const renderLoginForm = () => (
        <>
            <InputComponent
                id="id-input-email"
                label={t('email')}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                onKeyDown={handleKeyDown}
            />

            <InputComponent
                id="id-input-password"
                type="password"
                label={t('password')}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                onKeyDown={handleKeyDown}
            />

            <button type="button" className="forgot-password-button" onClick={handleForgotPassword}>
                {t('forgotPassword')}
            </button>

            <ButtonComponent
                label={loading ? t('submitting') : t('submit')}
                onClick={handleLogin}
                disabled={loading}
            />
        </>
    );

    const renderForgotPasswordForm = () => (
        <>
            <p className="login-description">{t('forgotPasswordDescription')}</p>

            <InputComponent
                id="id-input-email"
                label={t('email')}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                onKeyDown={handleKeyDown}
            />

            <ButtonComponent
                label={loading ? t('sendingResetCode') : t('sendResetCode')}
                onClick={handleRequestResetCode}
                disabled={loading}
            />

            <button type="button" className="login-text-button" onClick={handleBackToLogin}>
                {t('backToLogin')}
            </button>
        </>
    );

    const renderResetPasswordForm = () => (
        <>
            <p className="login-description">{t('resetPasswordDescription')}</p>

            <InputComponent
                id="id-input-reset-code"
                label={t('resetCode')}
                value={resetCode}
                onChange={(event) => setResetCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                onKeyDown={handleKeyDown}
            />

            <InputComponent
                id="id-input-new-password"
                type="password"
                label={t('newPassword')}
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                onKeyDown={handleKeyDown}
            />

            <InputComponent
                id="id-input-confirm-password"
                type="password"
                label={t('confirmPassword')}
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                onKeyDown={handleKeyDown}
            />

            <ButtonComponent
                label={loading ? t('resettingPassword') : t('resetPassword')}
                onClick={handleResetPassword}
                disabled={loading}
            />

            <button type="button" className="login-text-button" onClick={handleBackToLogin}>
                {t('backToLogin')}
            </button>
        </>
    );

    const renderResetSuccess = () => (
        <>
            <p className="login-description success-description">
                {t('resetPasswordSuccessDescription')}
            </p>

            <ButtonComponent
                label={t('backToLogin')}
                onClick={handleBackToLogin}
            />
        </>
    );
    
    return (
        <div className="login-container">
            <img 
                src="../../../assets/logo-inteira.png" 
                alt="Logo delay discounting"  
                className="logo" 
                style={{ width: '300px', height: '200px' }}
            />
            <div className="login-header">
                <h1 className="title">{t(titleKey)}</h1>
                <div className="language-selector" aria-label={t('languageLabel')}>
                    <button
                        type="button"
                        className={language === 'pt-BR' ? 'language-option active' : 'language-option'}
                        onClick={() => handleLanguageChange('pt-BR')}
                        aria-label={t('portugueseLanguage')}
                        title={t('portugueseLanguage')}
                    >
                        🇧🇷
                    </button>
                    <button
                        type="button"
                        className={language === 'en' ? 'language-option active' : 'language-option'}
                        onClick={() => handleLanguageChange('en')}
                        aria-label={t('englishLanguage')}
                        title={t('englishLanguage')}
                    >
                        🇺🇸
                    </button>
                </div>
            </div>
            
            {errorMessage && (
                <div className="login-message error-message">
                    {errorMessage}
                </div>
            )}

            {successMessage && (
                <div className="login-message success-message">
                    {successMessage}
                </div>
            )}

            {mode === 'login' && renderLoginForm()}
            {mode === 'forgotPassword' && renderForgotPasswordForm()}
            {mode === 'resetPassword' && renderResetPasswordForm()}
            {mode === 'resetSuccess' && renderResetSuccess()}
        </div>
    );
}
